<template>
	<v-container class="border-container">
		<v-row align="center" justify="center">
			<v-col cols="auto">
				<h1 style="margin-bottom: 30px;">NIS Score Map of</h1>
			</v-col>
			<v-col cols="auto">
				<div class="matrix-select">
					<v-select
						v-model="selectedType"
						:items="nisTypes"
						outlined
						dense
						style="width: 280px;"
					/>
				</div>
			</v-col>
		</v-row>

		<v-tabs background-color="transparent" light icons-and-text v-model="selectedTab">
			<v-tab>World Map</v-tab>
			<v-tab>Connection Map</v-tab>
		</v-tabs>

		<template v-if="selectedTab === 0">
			<nis-world-map-panel
				:key="selectedType"
				:title="worldMapConfig.title"
				:description="worldMapConfig.description"
				:focus-label="worldMapConfig.focusLabel"
			/>
		</template>

		<nis-connection-map
			v-else
			:score-key="scoreKey"
			:label="selectedType"
		/>
	</v-container>
</template>

<script>
import NisConnectionMap from "./NisConnectionMap.vue"
import NisWorldMapPanel from "./NisWorldMapPanel.vue"

const WORLD_MAPS = {
	"Governance & Risk Management": {
		title: "National cybersecurity policy & governance readiness",
		focusLabel: "Most relevant NCSI areas: cybersecurity policy development and coordination",
		description:
			"Country colors show the National Cyber Security Index (NCSI) score (0–100): how prepared a country’s central government is to prevent cyber threats and manage cyber incidents. Darker blue = stronger national cyber readiness. For Governance & Risk Management under NIS2, this is strongest context for national cybersecurity policy, strategy, and institutional oversight — the environment your organization operates in when setting policies, risk assessments, KPIs, and board reporting.",
	},
	"Incident Handling & Reporting": {
		title: "National cyber incident response readiness",
		focusLabel: "Most relevant NCSI areas: cyber incident response and reporting obligations",
		description:
			"Country colors show the National Cyber Security Index (NCSI) score (0–100). Darker blue = stronger national cyber readiness. For Incident Handling & Reporting under NIS2, use this as country context for national incident-response capacity, reporting obligations, and cooperation frameworks — relevant when your organization designs incident procedures, staff reporting channels, and serious-incident notification rules.",
	},
	"Business Continuity & Crisis Management": {
		title: "National cyber crisis & essential-services readiness",
		focusLabel: "Most relevant NCSI areas: cyber crisis management and essential services",
		description:
			"Country colors show the National Cyber Security Index (NCSI) score (0–100). Darker blue = stronger national cyber readiness. For Business Continuity & Crisis Management under NIS2, this reflects national crisis plans, exercises, and protection of essential/digital services — useful country context alongside your BCP/DR, backup, and remote-work controls.",
	},
	"Supply Chain & Third Parties": {
		title: "National supply-chain & digital-enabler cybersecurity",
		focusLabel: "Most relevant NCSI areas: supply chain cybersecurity and digital enablers",
		description:
			"Country colors show the National Cyber Security Index (NCSI) score (0–100). Darker blue = stronger national cyber readiness. For Supply Chain & Third Parties under NIS2, this provides country context for national expectations around supplier cybersecurity, cloud/digital enablers, and critical-service dependencies — relevant when assessing and contracting suppliers.",
	},
	"Technical & Operational Security": {
		title: "National technical cybersecurity capacity",
		focusLabel: "Most relevant NCSI areas: essential services, digital enablers, threat analysis",
		description:
			"Country colors show the National Cyber Security Index (NCSI) score (0–100). Darker blue = stronger national cyber readiness. For Technical & Operational Security under NIS2, this reflects national capacity around secure digital infrastructure, threat analysis, and protective requirements — context for your MFA, patching, access control, encryption, logging, and asset-management practices.",
	},
	"People, Training & HR Security": {
		title: "National cyber education & awareness readiness",
		focusLabel: "Most relevant NCSI areas: education, professional development, and awareness raising",
		description:
			"Country colors show the National Cyber Security Index (NCSI) score (0–100). Darker blue = stronger national cyber readiness. For People, Training & HR Security under NIS2, this is strongest context for national cybersecurity education, professional development, and public awareness programmes — the environment that supports your workforce training, HR lifecycle controls, and security responsibilities in contracts.",
	},
}

export default {
	name: "NisConnectionsMap",
	components: { NisConnectionMap, NisWorldMapPanel },
	data() {
		return {
			selectedType: "Governance & Risk Management",
			nisTypes: Object.keys(WORLD_MAPS),
			selectedTab: 0,
		}
	},
	computed: {
		worldMapConfig() {
			return WORLD_MAPS[this.selectedType] || WORLD_MAPS["Governance & Risk Management"]
		},
		scoreKey() {
			const map = {
				"Governance & Risk Management": "score1",
				"Incident Handling & Reporting": "score2",
				"Business Continuity & Crisis Management": "score3",
				"Supply Chain & Third Parties": "score4",
				"Technical & Operational Security": "score5",
				"People, Training & HR Security": "score6",
			}
			return map[this.selectedType] || "score1"
		},
	},
}
</script>

<style scoped>
.border-container {
	border: 1px solid black;
}
</style>
